import type { Building } from "building-types/building/buildingTypes";
import type { ElevationAnnotationParams } from "building-types/views/elevationAnnotationTypes";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { numMul } from "common/utils/nominalNumbers";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { aabb2New } from "geom/aabb2Types";
import { mat4LookAt } from "geom/mat4";
import type { Vec3 } from "geom/vec3";
import { getRenderSizeWithTitleBlock } from "lib/house/pdf/getRenderSizeWithTitleBlock";
import { orthographicDrawingBB } from "lib/house/pdf/renderBuildingPerspectiveDrawing";
import { getElevationScaleOverrideWithDefaultPageDimensions } from "lib/house/svg/getElevationScaleOverride";

// Takes a building and returns a scale factor for the elevation drawing
export async function getElevationScaleFactor({
  building,
  eyePos,
  propSelectors,
}: {
  building: Building;
  eyePos: Vec3;
  propSelectors?: SheetGeometrySelectorEffectMap[];
}) {
  const viewMatrix = mat4LookAt(eyePos, [0, 0, 0], [0, 0, 1]);
  const worldAABB = await orthographicDrawingBB(building, viewMatrix, true, propSelectors);
  const renderDims = getRenderSizeWithTitleBlock();
  const scale = getElevationScaleOverrideWithDefaultPageDimensions(building, false);
  const scaledWidth = (renderDims.renderWidth * scale) / 2;
  const scaledHeight = (renderDims.renderHeight * scale) / 2;
  const dwgCenterX = (worldAABB.minX + worldAABB.maxX) / 2;
  const dwgCenterY = (worldAABB.minY + worldAABB.maxY) / 2;
  const cX = dwgCenterX;
  const cY = dwgCenterY;
  const bb = aabb2New(cX - scaledWidth, cY - scaledHeight, cX + scaledWidth, cY + scaledHeight);
  const dwgWidth = bb.maxX - bb.minX;
  const dwgHeight = bb.maxY - bb.minY;
  const pageAspectRatio = renderDims.renderHeight / renderDims.renderWidth;
  const dwgAspectRatio = dwgHeight / dwgWidth;
  if (dwgAspectRatio > pageAspectRatio) {
    return renderDims.renderHeight / dwgHeight;
  } else {
    return renderDims.renderWidth / dwgWidth;
  }
}

export async function scaleElevationAnnotationParams(
  building: Building,
  eyePos: Vec3,
  elevationAnnotationParams: ElevationAnnotationParams,
  scaleFactor: number,
): Promise<ElevationAnnotationParams> {
  // Determine the exact scale of the elevation drawing in generateElevationSheetGeometry.ts
  const elevationScale = await getElevationScaleFactor({ building, eyePos });
  const relativeScale = elevationScale / scaleFactor;

  function scaleValue(value: SheetInches | undefined): SheetInches | undefined {
    return value !== undefined ? numMul(value, relativeScale) : undefined;
  }

  return {
    levelAnnotationMethod: elevationAnnotationParams.levelAnnotationMethod,
    levelAnnotationOffset: scaleValue(elevationAnnotationParams.levelAnnotationOffset),
    levelAnnotationVerticalSpacing: scaleValue(elevationAnnotationParams.levelAnnotationVerticalSpacing),
    boundingBoxElbowXSpacing: scaleValue(elevationAnnotationParams.boundingBoxElbowXSpacing),
    boundingBoxLabelXSpacing: scaleValue(elevationAnnotationParams.boundingBoxLabelXSpacing),
    labelTextWidth: scaleValue(elevationAnnotationParams.labelTextWidth),
  };
}
